import type { ChatSource } from "@/components/chat/use-chat-session"

/**
 * Client-side locale hardening for KB source attribution.
 *
 * The glue service already localizes source labels (per-locale, derived from
 * the page URL), but this is a belt-and-suspenders second layer so the *word*
 * the widget renders is guaranteed to match the active locale regardless of
 * what the server sent:
 *
 *   - a source whose URL points at a **different** locale is dropped (it would
 *     otherwise link to a page in the wrong language),
 *   - for a recognised **top-level** Ahead page, the generic label is
 *     re-derived from the URL path in the active locale — so a stale or
 *     wrong-language server label can't slip through,
 *   - deeper pages (a single biomarker/disease/article) keep the server label
 *     (the specific name only lives there; the URL already carries the locale),
 *   - a source with no URL can't be verified, so it's passed through untouched.
 *
 * Mirrors `glue/src/sources.ts` (`PAGE_LABELS` / `BOOKING_LABEL`) on purpose —
 * the two layers are independent.
 */

type Locale = "en" | "de" | "fr" | "nl"

const SUPPORTED_LOCALES: ReadonlySet<string> = new Set(["en", "de", "fr", "nl"])

function normalizeLocale(locale: string): Locale {
  switch (locale.toLowerCase()) {
    case "de":
      return "de"
    case "fr":
      return "fr"
    case "nl":
      return "nl"
    default:
      return "en"
  }
}

/** Localized labels for top-level Ahead pages, keyed by first path segment. */
const PAGE_LABELS: Record<string, Record<Locale, string>> = {
  pricing: {
    en: "Pricing",
    de: "Preise",
    fr: "Tarifs",
    nl: "Prijzen",
  },
  biomarkers: {
    en: "Biomarker database",
    de: "Biomarker-Datenbank",
    fr: "Base de biomarqueurs",
    nl: "Biomarker-database",
  },
  "what-we-detect": {
    en: "What we detect",
    de: "Was wir erkennen",
    fr: "Ce que nous détectons",
    nl: "Wat we opsporen",
  },
  insurance: {
    en: "Insurance coverage",
    de: "Kostenübernahme",
    fr: "Couverture d'assurance",
    nl: "Verzekeringsdekking",
  },
  tests: {
    en: "Tests & packages",
    de: "Tests & Pakete",
    fr: "Tests et forfaits",
    nl: "Tests & pakketten",
  },
  test: {
    en: "Tests & packages",
    de: "Tests & Pakete",
    fr: "Tests et forfaits",
    nl: "Tests & pakketten",
  },
  locations: {
    en: "Clinic locations",
    de: "Standorte",
    fr: "Nos cliniques",
    nl: "Locaties",
  },
  insights: {
    en: "Insights",
    de: "Magazin",
    fr: "Articles",
    nl: "Inzichten",
  },
  report: {
    en: "Sample report",
    de: "Musterbericht",
    fr: "Exemple de rapport",
    nl: "Voorbeeldrapport",
  },
}

const BOOKING_LABEL: Record<Locale, string> = {
  en: "Booking",
  de: "Buchung",
  fr: "Réservation",
  nl: "Boeking",
}

/**
 * Harden a server-provided source list against the active locale. Pure — safe
 * to call at SSE-attach time. Never throws on a malformed URL (keeps as-is).
 */
export function localizeSources(
  sources: ChatSource[],
  locale: string
): ChatSource[] {
  const loc = normalizeLocale(locale)
  const out: ChatSource[] = []

  for (const source of sources) {
    if (!source.url) {
      out.push(source)
      continue
    }

    let url: URL
    try {
      url = new URL(source.url)
    } catch {
      out.push(source)
      continue
    }

    // Booking host: localize the label, keep the link.
    if (url.hostname.toLowerCase() === "booking.aheadhealth.com") {
      out.push({ label: BOOKING_LABEL[loc], url: source.url })
      continue
    }

    const segments = url.pathname.split("/").filter(Boolean)
    // Canonical Ahead URLs are `/<country>/<locale>/...`.
    const urlLocale =
      segments.length >= 2 ? segments[1].toLowerCase() : undefined

    // Cross-locale leak: the link would open a page in another language.
    if (urlLocale && SUPPORTED_LOCALES.has(urlLocale) && urlLocale !== loc) {
      continue
    }

    const rest = segments.length >= 2 ? segments.slice(2) : segments

    // Top-level known page → re-derive the localized label (overrides server).
    if (rest.length === 1) {
      const entry = PAGE_LABELS[rest[0].toLowerCase()]
      if (entry) {
        out.push({ label: entry[loc], url: source.url })
        continue
      }
    }

    // Deep / unknown page → trust the server label (specific name).
    out.push(source)
  }

  return out
}
